import React, { useState, useEffect } from 'react';
import api from '../services/api';
import ModifierModal from './ModifierModal';

// Formulario de toma de pedidos, reutilizado por el Mesero y por el Cajero.
// showWaiterSelect=true habilita un selector para asignar el pedido a un mesero (uso del cajero).
const OrderForm = ({ showWaiterSelect = false, onOrderCreated }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedWaiterId, setSelectedWaiterId] = useState('');
  const [cart, setCart] = useState([]);
  const [orderType, setOrderType] = useState('dine_in');
  const [customerName, setCustomerName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [modifierModal, setModifierModal] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchTables();
    if (showWaiterSelect) fetchWaiters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTables = async () => {
    try {
      const res = await api.get('/tables');
      setTables(res.data.data || []);
    } catch (err) {
      console.error('Error cargando mesas:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get('/menu/SUC-001');
      if (res.data.status) {
        const cats = res.data.data.categories;
        setCategories(cats);
        setActiveCategory(cats[0]?.id);
        const all = cats.flatMap(c => c.products);
        setProducts(all);
      }
    } catch (err) {
      console.error('Error cargando productos:', err);
    }
  };

  const fetchWaiters = () => {
    api.get('/waiters').then(res => {
      setWaiters(res.data.data || []);
    }).catch(() => {});
  };

  const handleAddProduct = (product) => {
    if (product.has_modifiers && product.modifiers?.length > 0) {
      setModifierModal(product);
    } else {
      addToCart(product, [], '');
    }
  };

  const addToCart = (product, modifiers, itemNotes) => {
    const existingIndex = cart.findIndex(item =>
      item.product_id === product.id &&
      JSON.stringify(item.modifiers) === JSON.stringify(modifiers)
    );

    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      if (itemNotes) newCart[existingIndex].notes = itemNotes;
      setCart(newCart);
    } else {
      setCart([...cart, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        notes: itemNotes || '',
        modifiers: modifiers || [],
      }]);
    }
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const updateQuantity = (index, delta) => {
    setCart(cart.map((item, i) => {
      if (i === index) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const updateItemNotes = (index, note) => {
    setCart(cart.map((item, i) => i === index ? { ...item, notes: note } : item));
  };

  const submitOrder = async () => {
    if (cart.length === 0) return;
    setLoading(true);

    try {
      const items = cart.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        notes: item.notes || undefined,
        modifiers: item.modifiers.length > 0
          ? item.modifiers.map(m => ({ modifier_option_id: m.modifier_option_id, quantity: m.quantity || 1 }))
          : undefined,
      }));

      await api.post('/orders', {
        branch_id: 1,
        table_id: selectedTable?.id || null,
        customer_name: customerName || undefined,
        order_type: orderType,
        notes: notes || undefined,
        waiter_id: showWaiterSelect && selectedWaiterId ? selectedWaiterId : undefined,
        items,
      });

      setSuccess('¡Pedido enviado exitosamente!');
      setCart([]);
      setSelectedTable(null);
      setSelectedWaiterId('');
      setCustomerName('');
      setNotes('');
      if (onOrderCreated) onOrderCreated();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      alert(err.response?.data?.message || 'Error al enviar pedido');
    } finally {
      setLoading(false);
    }
  };

  const cartTotal = cart.reduce((sum, item) => {
    const modifiersTotal = item.modifiers?.reduce((mSum, m) => mSum + (m.price_adjustment || 0), 0) || 0;
    return sum + ((item.price + modifiersTotal) * item.quantity);
  }, 0);

  const activeCat = categories.find(c => c.id === activeCategory);

  return (
    <div>
      {success && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)', border: '1px solid #10b981',
          color: '#10b981', padding: 16, borderRadius: 10, marginBottom: 20,
        }}>
          {success}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24 }}>
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Tipo de orden</label>
                <select value={orderType} onChange={e => setOrderType(e.target.value)} className="input" style={{ width: 160 }}>
                  <option value="dine_in">🪑 Comer aquí</option>
                  <option value="takeout">📦 Para llevar</option>
                  <option value="delivery">🛵 Delivery</option>
                </select>
              </div>
              {orderType === 'dine_in' && (
                <div>
                  <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Mesa</label>
                  <select value={selectedTable?.id || ''} onChange={e => {
                    const t = tables.find(t => t.id === parseInt(e.target.value));
                    setSelectedTable(t || null);
                  }} className="input" style={{ width: 160 }}>
                    <option value="">Seleccionar...</option>
                    {tables.filter(t => t.status === 'free').map(t => (
                      <option key={t.id} value={t.id}>Mesa {t.number}{t.name ? ` — ${t.name}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              {showWaiterSelect && (
                <div>
                  <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Asignar a mesero</label>
                  <select value={selectedWaiterId} onChange={e => setSelectedWaiterId(e.target.value)} className="input" style={{ width: 200 }}>
                    <option value="">Sin asignar (yo mismo)</option>
                    {waiters.map(w => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Cliente</label>
                <input type="text" placeholder="Nombre del cliente (opcional)" className="input" value={customerName} onChange={e => setCustomerName(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Categorías */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto' }}>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                style={{
                  padding: '8px 16px', borderRadius: 20, border: 'none',
                  background: activeCategory === cat.id ? '#38bdf8' : '#1e293b',
                  color: activeCategory === cat.id ? '#0f172a' : '#94a3b8',
                  fontWeight: 600, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap',
                }}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>

          {/* Productos */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {activeCat?.products?.map(product => (
              <div
                key={product.id}
                onClick={() => handleAddProduct(product)}
                className="card"
                style={{ cursor: 'pointer', padding: 16, transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#38bdf8'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{
                  width: '100%', height: 120, borderRadius: 10,
                  background: product.image_url ? `url(${product.image_url}) center/cover` : '#334155',
                  marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32,
                }}>
                  {!product.image_url && '🍽️'}
                </div>
                <h4 style={{ color: '#e2e8f0', fontSize: 14, marginBottom: 4 }}>{product.name}</h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ color: '#fbbf24', fontWeight: 700, fontSize: 16 }}>${product.price?.toFixed(2)}</p>
                  {product.has_modifiers && (
                    <span style={{ color: '#a855f7', fontSize: 11 }}>⚙️ Opciones</span>
                  )}
                </div>
              </div>
            ))}
            {activeCat?.products?.length === 0 && (
              <p style={{ color: '#64748b', textAlign: 'center', padding: 30 }}>No hay productos en esta categoría</p>
            )}
          </div>
        </div>

        {/* Carrito */}
        <div className="card" style={{ position: 'sticky', top: 24, height: 'fit-content' }}>
          <h3 style={{ color: '#e2e8f0', marginBottom: 16, fontSize: 18 }}>🛒 Pedido Actual</h3>

          {cart.length === 0 ? (
            <p style={{ color: '#64748b', textAlign: 'center', padding: 30 }}>Selecciona productos del menú</p>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16, maxHeight: 400, overflowY: 'auto' }}>
                {cart.map((item, index) => (
                  <div key={index} style={{ background: '#0f172a', borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 14 }}>{item.name}</span>
                      <button onClick={() => removeFromCart(index)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }}>✕</button>
                    </div>
                    {item.modifiers?.length > 0 && (
                      <div style={{ marginBottom: 6 }}>
                        {item.modifiers.map((mod, mi) => (
                          <span key={mi} style={{ color: '#a855f7', fontSize: 11, marginRight: 6 }}>
                            + {mod.option_name || mod.modifier_option_id}
                          </span>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button onClick={() => updateQuantity(index, -1)} style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: '#334155', color: '#e2e8f0', cursor: 'pointer', fontWeight: 'bold' }}>−</button>
                        <span style={{ color: '#e2e8f0', fontWeight: 600, minWidth: 20, textAlign: 'center' }}>{item.quantity}</span>
                        <button onClick={() => updateQuantity(index, 1)} style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: '#334155', color: '#e2e8f0', cursor: 'pointer', fontWeight: 'bold' }}>+</button>
                      </div>
                      <span style={{ color: '#fbbf24', fontWeight: 600 }}>
                        ${((item.price + (item.modifiers?.reduce((s, m) => s + (m.price_adjustment || 0), 0) || 0)) * item.quantity).toFixed(2)}
                      </span>
                    </div>
                    <input type="text" placeholder="Notas: sin cebolla, etc." className="input" style={{ fontSize: 12, padding: '8px 10px' }} value={item.notes} onChange={e => updateItemNotes(index, e.target.value)} />
                  </div>
                ))}
              </div>

              <div style={{ borderTop: '1px solid #334155', paddingTop: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 16 }}>Total</span>
                  <span style={{ color: '#fbbf24', fontWeight: 700, fontSize: 18 }}>${cartTotal.toFixed(2)}</span>
                </div>
              </div>

              <textarea placeholder="Notas generales del pedido..." className="input" style={{ marginBottom: 12, minHeight: 60, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} />

              <button onClick={submitOrder} className="btn btn-success" style={{ width: '100%', padding: 14, fontSize: 16 }} disabled={loading}>
                {loading ? 'Enviando...' : '🚀 Enviar Pedido'}
              </button>
            </>
          )}
        </div>
      </div>

      {modifierModal && (
        <ModifierModal
          product={modifierModal}
          onClose={() => setModifierModal(null)}
          onAdd={addToCart}
        />
      )}
    </div>
  );
};

export default OrderForm;